import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useI18n } from '@/lib/i18n'
import { isNumericOption, isStringEnumOption } from '@/lib/play.ts'
import { ResponseOption } from '@/lib/types'
import { getNumericResponseLabel } from '@/lib/response-choice-label'
import { cn } from '@/lib/utils'

interface ResponseInputProps {
  option: ResponseOption
  value: string | number
  onChange: (value: string) => void
}

const SHERIFF_DIRECTION_ACTION = 'action_sheriff_decide_order'
const SHERIFF_DIRECTION_PRESENTATION = {
  counter_clockwise: {
    labelKey: 'game.sheriffDirectionCounterClockwise',
    order: 0,
  },
  clockwise: {
    labelKey: 'game.sheriffDirectionClockwise',
    order: 1,
  },
} as const

function getStringEnumChoices(
  actionType: string,
  possibleValues: ReadonlyArray<string | number>,
) {
  const values = possibleValues.map(String)
  if (actionType !== SHERIFF_DIRECTION_ACTION) {
    return values.map(value => ({
      labelKey: `game.luckySkill.${value}`,
      value,
    }))
  }

  return values
    .map((value) => {
      const presentation
        = SHERIFF_DIRECTION_PRESENTATION[
          value as keyof typeof SHERIFF_DIRECTION_PRESENTATION
        ]
      return {
        labelKey: presentation?.labelKey ?? `game.luckySkill.${value}`,
        order: presentation?.order ?? Number.MAX_SAFE_INTEGER,
        value,
      }
    })
    .sort((left, right) => left.order - right.order)
}

export function ResponseInput({ option, value, onChange }: ResponseInputProps) {
  const { t } = useI18n()

  if (isNumericOption(option)) {
    const currentValue = String(value ?? '')

    return (
      <div className="flex flex-wrap gap-2">
        {option.possibleValues.map((val) => {
          const label = getNumericResponseLabel(val, option.dbAction, t)
          const stringVal = String(val)
          const isSelected = currentValue === stringVal

          return (
            <Button
              key={val}
              type="button"
              data-testid="response-choice"
              data-response-tag={option.tag}
              data-response-value={stringVal}
              aria-pressed={isSelected}
              variant="outline"
              className={cn(
                'transition-all',
                isSelected && '!border-primary !bg-primary/10 font-bold ring-2 ring-primary ring-offset-1',
              )}
              onClick={() => onChange(stringVal)}
            >
              {label}
            </Button>
          )
        })}
      </div>
    )
  }

  if (isStringEnumOption(option)) {
    const currentValue = String(value ?? '')
    const choices = getStringEnumChoices(
      option.dbAction,
      option.possibleValues,
    )

    return (
      <div className="flex flex-wrap gap-2">
        {choices.map(({ labelKey, value: choiceValue }) => {
          const isSelected = currentValue === choiceValue
          return (
            <Button
              key={choiceValue}
              type="button"
              data-testid="response-choice"
              data-response-tag={option.tag}
              data-response-value={choiceValue}
              aria-pressed={isSelected}
              variant="outline"
              className={cn(
                'transition-all',
                isSelected && '!border-primary !bg-primary/10 font-bold ring-2 ring-primary ring-offset-1',
              )}
              onClick={() => onChange(choiceValue)}
            >
              {t(labelKey)}
            </Button>
          )
        })}
      </div>
    )
  }

  return (
    <Textarea
      data-testid="response-text"
      data-response-tag={option.tag}
      className="max-h-32 min-h-[120px] resize-none overflow-y-auto overscroll-contain bg-background/50 backdrop-blur-sm [scrollbar-gutter:stable]"
      value={String(value || '')}
      onChange={e => onChange(e.target.value)}
    />
  )
}
